import { IBalusterGeometry, IRailingConfig, IRailingGeometry, IStairConfig, IStepGeometry, IVec3 } from '@stair-craft/common/util';

import { balusterCount, treadBalusterRuns } from '../shared-baluster-count/baluster-count';
import { railLineOffset } from '../shared-board-band/board-band';
import { IFlightRail, flightRail, flightRailY } from '../shared-flight-rail/flight-rail';

/** Step-derived anchor values, independent of railing side. */
interface IUShapeStepAnchors {
    hasFlight1: boolean;
    firstF1: IStepGeometry | null;
    entryZ: number;
    firstStepY: number;
}

/** Turn-level values (heights along the turn), independent of railing side. */
interface IUShapeTurnLevels {
    turnEntryY: number;
    landingY: number;
    riserH: number;
    isWinderTurn: boolean;
    turnPostY: number;
}

/** Side-independent inputs threaded through the per-side builder. */
interface IUShapeRailingBase extends IUShapeStepAnchors, IUShapeTurnLevels {
    rc: IRailingConfig;
    config: IStairConfig;
    sw: number;
    isRight: boolean;
    turnStartZ: number;
    f2StartZ: number;
    geoMaxZ: number;
    flight1Steps: IStepGeometry[];
    turnSteps: IStepGeometry[];
    flight2Steps: IStepGeometry[];
}

/** Per-side working context shared by the U-shape railing block helpers. */
interface IUShapeRailingContext extends IUShapeRailingBase {
    side: 'left' | 'right';
    isOuter: boolean;
    halfPost: number;
    f1X: number;
    f2X: number;
    f1hAz: number;
    f1hAy: number;
    f1hBz: number;
    f1hBy: number;
    /** The one handrail line over flight 2, along −z (flight 2 climbs toward −Z). */
    f2Rail: IFlightRail;
    balusters: IBalusterGeometry[];
    posts: IBalusterGeometry[];
    handrailPoints: IVec3[];
}

/**
 * Resolves the step-derived anchor values (entry position, first-step level).
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function resolveStepAnchors(flight1Steps: IStepGeometry[], turnSteps: IStepGeometry[], flight2Steps: IStepGeometry[]): IUShapeStepAnchors {
    const hasFlight1: boolean = flight1Steps.length > 0;
    const firstF1: IStepGeometry | null = hasFlight1 ? (flight1Steps.at(0) ?? null) : null;
    const firstStep: IStepGeometry | null = firstF1 ?? turnSteps.at(0) ?? flight2Steps.at(0) ?? null;
    const entryZ: number = firstF1?.position.z ?? 0;
    const firstStepY: number = firstStep?.position.y ?? 0;
    return { hasFlight1, firstF1, entryZ, firstStepY };
}

/**
 * Resolves the turn heights (entry/landing Y, post Y, winder flag).
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function resolveTurnLevels(
    flight1Steps: IStepGeometry[],
    turnSteps: IStepGeometry[],
    firstStepY: number,
    config: IStairConfig
): IUShapeTurnLevels {
    const lastF1: IStepGeometry | null = flight1Steps.length > 0 ? flight1Steps[flight1Steps.length - 1] : null;
    // Y the user is at when entering the turn — top of flight 1 if it exists,
    // otherwise the first turn step's level (which the bottom post already anchors to).
    const turnEntryY: number = lastF1?.position.y ?? firstStepY;
    const entryRiser: number = lastF1?.riserHeight ?? turnSteps.at(0)?.riserHeight ?? 0;
    const landingY: number = turnEntryY + entryRiser;
    const isWinderTurn: boolean = config.turnType === 'winder' && turnSteps.length > 0;
    // At the turn edge the nosing line reaches the first turn tread's top — the landing or the
    // first winder — so flight 1's rail ends the rail height above it on either turn.
    const turnPostY: number = landingY;
    return { turnEntryY, landingY, isWinderTurn, turnPostY, riserH: entryRiser };
}

/**
 * Per-side X of flight 2. Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function computeFlight2X(isRight: boolean, isOuter: boolean, sw: number, config: IStairConfig, halfPost: number): number {
    if (isRight) {
        return isOuter ? 2 * sw + config.flightGap - halfPost : sw + config.flightGap + halfPost;
    }
    // Flight 2 spans x ∈ [−(sw + gap), −gap]: the inner rail stands inside it, past its inner edge.
    return isOuter ? -(sw + config.flightGap - halfPost) : -(config.flightGap + halfPost);
}

/**
 * Base Y of the winder step underneath a given path fraction. Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function winderYAtFrac(turnSteps: IStepGeometry[], pathStartY: number, totalFrac: number): number {
    if (turnSteps.length === 0) {
        return pathStartY;
    }
    const idx: number = Math.min(turnSteps.length - 1, Math.max(0, Math.floor(totalFrac * turnSteps.length)));
    return turnSteps[idx].position.y;
}

/** Flight 2's handrail height above a plan Z. */
function flight2RailY(ctx: IUShapeRailingContext, z: number): number {
    return flightRailY(ctx.f2Rail, -z);
}

/** A newel standing at `baseY` tall enough to carry the higher of its two rail ends. */
function newelHeight(ctx: IUShapeRailingContext, baseY: number, upperRailY: number): number {
    return Math.max(ctx.rc.height, upperRailY - baseY);
}

/**
 * Flight 2's rail line: from the rail height above the first tread's nose tip — a nose length
 * toward +Z from the turn edge — to the top of the top post.
 */
function uShapeFlight2Rail(base: IUShapeRailingBase, topPost: IBalusterGeometry): IFlightRail {
    const first: IStepGeometry = base.flight2Steps[0];
    return flightRail(first, -(base.f2StartZ + first.noseLength), base.rc.height, {
        along: -topPost.position.z,
        y: topPost.position.y + topPost.height,
    });
}

/** The landing newel where flight 2 starts: the landing rail comes in at its side, flight 2's rail leaves its top. */
function pushFlight2Newel(ctx: IUShapeRailingContext, z: number): void {
    const { f2X, landingY, rc } = ctx;
    const flightStartY: number = flight2RailY(ctx, z);
    ctx.handrailPoints.push({ x: f2X, y: landingY + rc.height, z });
    ctx.handrailPoints.push({ x: f2X, y: flightStartY, z });
    ctx.posts.push({ position: { x: f2X, y: landingY, z }, height: newelHeight(ctx, landingY, flightStartY), rotation: 0 });
}

/**
 * Flight-1 balusters, interpolated between the bottom post top and the turn entry.
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function pushFlight1Balusters(ctx: IUShapeRailingContext): void {
    if (!ctx.hasFlight1 || !ctx.firstF1) {
        return;
    }
    const rc: IRailingConfig = ctx.rc;
    const f1Td: number = ctx.firstF1.treadDepth;
    const f1SpanZ: number = ctx.f1hBz - ctx.f1hAz;
    const flight: { stringerType: IStairConfig['stringerType']; axis: 'z' } = { stringerType: ctx.config.stringerType, axis: 'z' };
    for (const bz of treadBalusterRuns(ctx.f1hAz + rc.postWidth / 2, ctx.f1hBz, ctx.flight1Steps, flight, rc)) {
        const si: number = Math.min(Math.floor((bz - ctx.firstF1.position.z) / f1Td), ctx.flight1Steps.length - 1);
        const baseY: number = ctx.flight1Steps[si].position.y;
        const frac: number = (bz - ctx.f1hAz) / f1SpanZ;
        const hY: number = ctx.f1hAy + frac * (ctx.f1hBy - ctx.f1hAy);
        ctx.balusters.push({ position: { x: ctx.f1X, y: baseY, z: bz }, height: Math.max(rc.height / 2, hY - baseY), rotation: 0 });
    }
}

/**
 * Outer winder turn: U-path along outer walls (Z wall → far X wall → Z wall back) with balusters.
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function pushWinderOuterRailing(ctx: IUShapeRailingContext, pathStartY: number, pathEndY: number): void {
    const rc: IRailingConfig = ctx.rc;
    const { f1X, f2X, turnStartZ, f2StartZ, config, halfPost, sw, turnSteps } = ctx;

    // The winder back wall is at turnStartZ + sw + flightGap/2, matching
    // generateWinderSteps so the railing rests on the actual tread edge.
    const farZ: number = turnStartZ + sw + config.flightGap / 2 - halfPost;
    const seg1Len: number = farZ - turnStartZ;
    const seg2Len: number = Math.abs(f2X - f1X);
    const seg3Len: number = farZ - f2StartZ;
    const totalLen: number = seg1Len + seg2Len + seg3Len;

    // Handrail along U-path
    const pathPointCount: number = Math.max(12, Math.round(totalLen / 80));
    for (let i: number = 0; i <= pathPointCount; i++) {
        const frac: number = i / pathPointCount;
        const dist: number = frac * totalLen;
        const y: number = pathStartY + frac * (pathEndY - pathStartY) + rc.height;

        if (dist <= seg1Len) {
            ctx.handrailPoints.push({ x: f1X, z: turnStartZ + dist, y });
        } else if (dist <= seg1Len + seg2Len) {
            const xFrac: number = (dist - seg1Len) / seg2Len;
            ctx.handrailPoints.push({ x: f1X + (f2X - f1X) * xFrac, z: farZ, y });
        } else {
            const zBack: number = dist - seg1Len - seg2Len;
            ctx.handrailPoints.push({ x: f2X, z: farZ - zBack, y });
        }
    }

    // Balusters along segment 1 (flight 1 Z wall)
    const seg1BalCount: number = balusterCount(seg1Len, rc);
    for (let i: number = 0; i < seg1BalCount; i++) {
        const frac: number = (i + 0.5) / seg1BalCount;
        const totalFrac: number = (seg1Len * frac) / totalLen;
        const handrailY: number = pathStartY + totalFrac * (pathEndY - pathStartY) + rc.height;
        const baseY: number = winderYAtFrac(turnSteps, pathStartY, totalFrac);
        ctx.balusters.push({
            position: { x: f1X, y: baseY, z: turnStartZ + seg1Len * frac },
            height: Math.max(rc.height / 2, handrailY - baseY),
            rotation: 0,
        });
    }

    // Balusters along segment 2 (far X wall)
    const seg2BalCount: number = balusterCount(seg2Len, rc);
    for (let i: number = 0; i < seg2BalCount; i++) {
        const frac: number = (i + 0.5) / seg2BalCount;
        const totalFrac: number = (seg1Len + seg2Len * frac) / totalLen;
        const handrailY: number = pathStartY + totalFrac * (pathEndY - pathStartY) + rc.height;
        const baseY: number = winderYAtFrac(turnSteps, pathStartY, totalFrac);
        ctx.balusters.push({
            position: { x: f1X + (f2X - f1X) * frac, y: baseY, z: farZ },
            height: Math.max(rc.height / 2, handrailY - baseY),
            rotation: 0,
        });
    }

    // Balusters along segment 3 (flight 2 Z wall back)
    const seg3BalCount: number = balusterCount(seg3Len, rc);
    for (let i: number = 0; i < seg3BalCount; i++) {
        const frac: number = (i + 0.5) / seg3BalCount;
        const totalFrac: number = (seg1Len + seg2Len + seg3Len * frac) / totalLen;
        const handrailY: number = pathStartY + totalFrac * (pathEndY - pathStartY) + rc.height;
        const baseY: number = winderYAtFrac(turnSteps, pathStartY, totalFrac);
        ctx.balusters.push({
            position: { x: f2X, y: baseY, z: farZ - seg3Len * frac },
            height: Math.max(rc.height / 2, handrailY - baseY),
            rotation: 0,
        });
    }
}

/**
 * Inner winder turn: U-path along the gap walls with balusters.
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function pushWinderInnerRailing(ctx: IUShapeRailingContext, pathStartY: number, pathEndY: number): void {
    const rc: IRailingConfig = ctx.rc;
    const { turnStartZ, f2StartZ, config, halfPost, isRight, sw, turnSteps } = ctx;

    const innerZ: number = turnStartZ + config.flightGap / 2 + halfPost;
    const innerF1X: number = isRight ? sw - halfPost : halfPost;
    const innerF2X: number = isRight ? sw + config.flightGap + halfPost : -(config.flightGap + halfPost);
    const seg1Len: number = innerZ - turnStartZ;
    const seg2Len: number = Math.abs(innerF2X - innerF1X);
    const seg3Len: number = innerZ - f2StartZ;
    const totalLen: number = seg1Len + seg2Len + seg3Len;

    // Handrail along inner U-path
    const pathPointCount: number = Math.max(8, Math.round(totalLen / 80));
    for (let i: number = 0; i <= pathPointCount; i++) {
        const frac: number = i / pathPointCount;
        const dist: number = frac * totalLen;
        const y: number = pathStartY + frac * (pathEndY - pathStartY) + rc.height;

        if (dist <= seg1Len) {
            ctx.handrailPoints.push({ x: innerF1X, z: turnStartZ + dist, y });
        } else if (dist <= seg1Len + seg2Len) {
            const xFrac: number = (dist - seg1Len) / seg2Len;
            ctx.handrailPoints.push({ x: innerF1X + (innerF2X - innerF1X) * xFrac, z: innerZ, y });
        } else {
            const zBack: number = dist - seg1Len - seg2Len;
            ctx.handrailPoints.push({ x: innerF2X, z: innerZ - zBack, y });
        }
    }

    // Balusters along inner segments
    const seg1BalCount: number = balusterCount(seg1Len, rc);
    for (let i: number = 0; i < seg1BalCount; i++) {
        const frac: number = (i + 0.5) / seg1BalCount;
        const totalFrac: number = (seg1Len * frac) / totalLen;
        const handrailY: number = pathStartY + totalFrac * (pathEndY - pathStartY) + rc.height;
        const baseY: number = winderYAtFrac(turnSteps, pathStartY, totalFrac);
        ctx.balusters.push({
            position: { x: innerF1X, y: baseY, z: turnStartZ + seg1Len * frac },
            height: Math.max(rc.height / 2, handrailY - baseY),
            rotation: 0,
        });
    }

    const seg2BalCount: number = balusterCount(seg2Len, rc);
    for (let i: number = 0; i < seg2BalCount; i++) {
        const frac: number = (i + 0.5) / seg2BalCount;
        const totalFrac: number = (seg1Len + seg2Len * frac) / totalLen;
        const handrailY: number = pathStartY + totalFrac * (pathEndY - pathStartY) + rc.height;
        const baseY: number = winderYAtFrac(turnSteps, pathStartY, totalFrac);
        ctx.balusters.push({
            position: { x: innerF1X + (innerF2X - innerF1X) * frac, y: baseY, z: innerZ },
            height: Math.max(rc.height / 2, handrailY - baseY),
            rotation: 0,
        });
    }

    const seg3BalCount: number = balusterCount(seg3Len, rc);
    for (let i: number = 0; i < seg3BalCount; i++) {
        const frac: number = (i + 0.5) / seg3BalCount;
        const totalFrac: number = (seg1Len + seg2Len + seg3Len * frac) / totalLen;
        const handrailY: number = pathStartY + totalFrac * (pathEndY - pathStartY) + rc.height;
        const baseY: number = winderYAtFrac(turnSteps, pathStartY, totalFrac);
        ctx.balusters.push({
            position: { x: innerF2X, y: baseY, z: innerZ - seg3Len * frac },
            height: Math.max(rc.height / 2, handrailY - baseY),
            rotation: 0,
        });
    }
}

/**
 * Outer landing turn: U-path along the landing edges (far Z, then back to flight 2) with balusters.
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function pushLandingOuterRailing(ctx: IUShapeRailingContext): void {
    const rc: IRailingConfig = ctx.rc;
    const { f1X, f2X, turnStartZ, f2StartZ, landingY, sw, halfPost } = ctx;

    // Outer: go along far Z edge of landing, then back to flight 2
    const farZ: number = turnStartZ + sw - halfPost;
    ctx.handrailPoints.push({ x: f1X, y: landingY + rc.height, z: farZ });
    ctx.posts.push({ position: { x: f1X, y: landingY, z: farZ }, height: rc.height, rotation: 0 });

    ctx.handrailPoints.push({ x: f2X, y: landingY + rc.height, z: farZ });
    ctx.posts.push({ position: { x: f2X, y: landingY, z: farZ }, height: rc.height, rotation: 0 });

    pushFlight2Newel(ctx, f2StartZ);

    // Balusters along Z-edge (flight 1 side)
    const zEdgeLen: number = farZ - turnStartZ;
    const zBalCount: number = balusterCount(zEdgeLen, rc);
    for (let i: number = 1; i < zBalCount; i++) {
        ctx.balusters.push({
            position: { x: f1X, y: landingY, z: turnStartZ + (zEdgeLen * i) / zBalCount },
            height: rc.height,
            rotation: 0,
        });
    }

    // Balusters along X-edge (far Z)
    const xEdgeStart: number = Math.min(f1X, f2X);
    const xEdgeEnd: number = Math.max(f1X, f2X);
    const xEdgeLen: number = xEdgeEnd - xEdgeStart;
    const xBalCount: number = balusterCount(xEdgeLen, rc);
    for (let i: number = 1; i < xBalCount; i++) {
        ctx.balusters.push({
            position: { x: xEdgeStart + (xEdgeLen * i) / xBalCount, y: landingY, z: farZ },
            height: rc.height,
            rotation: 0,
        });
    }

    // Balusters along Z-edge (flight 2 side, from farZ back to f2StartZ)
    const z2EdgeLen: number = farZ - f2StartZ;
    const z2BalCount: number = balusterCount(z2EdgeLen, rc);
    for (let i: number = 1; i < z2BalCount; i++) {
        ctx.balusters.push({
            position: { x: f2X, y: landingY, z: farZ - (z2EdgeLen * i) / z2BalCount },
            height: rc.height,
            rotation: 0,
        });
    }
}

/**
 * Inner landing turn: connect the handrail at the near edge where flights meet the landing, with balusters.
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function pushLandingInnerRailing(ctx: IUShapeRailingContext): void {
    const rc: IRailingConfig = ctx.rc;
    const { f1X, f2X, turnStartZ, landingY } = ctx;
    // The edge between the flights is the landing's own edge: the row runs in from it over the
    // landing as far as along a stair edge, so no plank or baluster hangs past it.
    const lineZ: number = turnStartZ + ctx.halfPost;

    ctx.handrailPoints.push({ x: f1X, y: landingY + rc.height, z: turnStartZ });
    ctx.handrailPoints.push({ x: f1X, y: landingY + rc.height, z: lineZ });
    ctx.posts.push({ position: { x: f1X, y: landingY, z: turnStartZ }, height: rc.height, rotation: 0 });

    pushFlight2Newel(ctx, lineZ);

    // Balusters along inner X-edge (between flights)
    const innerXStart: number = Math.min(f1X, f2X);
    const innerXEnd: number = Math.max(f1X, f2X);
    const innerXLen: number = innerXEnd - innerXStart;
    const innerXBalCount: number = balusterCount(innerXLen, rc);
    for (let i: number = 1; i < innerXBalCount; i++) {
        ctx.balusters.push({
            position: { x: innerXStart + (innerXLen * i) / innerXBalCount, y: landingY, z: lineZ },
            height: rc.height,
            rotation: 0,
        });
    }
}

/**
 * Flight-2 balusters interpolated from the turn exit to the top post (flight 2 runs along -Z).
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function pushFlight2Balusters(ctx: IUShapeRailingContext, turnExitPt: IVec3, topPost: IBalusterGeometry, topPostTopY: number): void {
    if (ctx.flight2Steps.length === 0) {
        return;
    }
    const rc: IRailingConfig = ctx.rc;
    const f2hAz: number = turnExitPt.z;
    const f2hAy: number = turnExitPt.y;
    const f2hBz: number = topPost.position.z;
    const f2hBy: number = topPostTopY;
    const f2SpanZ: number = f2hAz - f2hBz; // flight 2 goes -Z
    const flight: { stringerType: IStairConfig['stringerType']; axis: 'z' } = { stringerType: ctx.config.stringerType, axis: 'z' };
    // On the inner side a landing's post stands at the turn exit; the row starts at its face.
    const startZ: number = f2hAz - (ctx.config.turnType === 'landing' && !ctx.isOuter ? rc.postWidth / 2 : 0);
    for (const bz of treadBalusterRuns(startZ, f2hBz + rc.postWidth / 2, ctx.flight2Steps, flight, rc)) {
        const frac: number = (f2hAz - bz) / f2SpanZ;
        const si: number = Math.min(Math.floor(frac * ctx.flight2Steps.length), ctx.flight2Steps.length - 1);
        const baseY: number = ctx.flight2Steps[si].position.y;
        const hY: number = f2hAy + frac * (f2hBy - f2hAy);
        ctx.balusters.push({ position: { x: ctx.f2X, y: baseY, z: bz }, height: Math.max(rc.height / 2, hY - baseY), rotation: 0 });
    }
}

/**
 * Turn transition (winder or landing) for one side. Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function pushTurnTransition(ctx: IUShapeRailingContext): void {
    const rc: IRailingConfig = ctx.rc;
    const { f1X, f2X, turnStartZ, f2StartZ, turnPostY, isOuter, isWinderTurn } = ctx;

    if (isWinderTurn) {
        ctx.handrailPoints.push({ x: f1X, y: turnPostY + rc.height, z: turnStartZ });

        // The winder path starts where flight 1's rail ends and lands on flight 2's rail line.
        const pathStartY: number = turnPostY;
        const pathEndY: number = flight2RailY(ctx, f2StartZ) - rc.height;

        if (isOuter) {
            pushWinderOuterRailing(ctx, pathStartY, pathEndY);
        } else {
            pushWinderInnerRailing(ctx, pathStartY, pathEndY);
        }

        // End point for transition to flight 2
        ctx.handrailPoints.push({ x: f2X, y: pathEndY + rc.height, z: f2StartZ });
    } else {
        // Landing: U-path along landing edges
        ctx.handrailPoints.push({ x: f1X, y: turnPostY + rc.height, z: turnStartZ });
        ctx.posts.push({ position: { x: f1X, y: turnPostY, z: turnStartZ }, height: rc.height, rotation: 0 });

        if (isOuter) {
            pushLandingOuterRailing(ctx);
        } else {
            pushLandingInnerRailing(ctx);
        }
    }
}

/**
 * Builds the full railing for one side (flight 1 → turn → flight 2).
 * Extracted from `generateUShapeRailings` to keep its complexity low.
 */
function buildSideRailing(base: IUShapeRailingBase, side: 'left' | 'right'): IRailingGeometry {
    const { rc, sw, isRight, turnStartZ, turnPostY, entryZ, firstStepY, config, geoMaxZ } = base;

    const isOuter: boolean = isRight ? side === 'left' : side === 'right';
    // Distance of the rail line from the stair edge: the тетива axis, or half a post over a kosour.
    const halfPost: number = railLineOffset(config.stringerType, rc);
    const f1X: number = side === 'left' ? halfPost : sw - halfPost;
    const f2X: number = computeFlight2X(isRight, isOuter, sw, config, halfPost);
    // Bottom post reaches the handrail level above the first stair tread so the
    // handrail starts coplanar with the first step, not one riser below.
    const bottomPostTopY: number = firstStepY + rc.height;

    // ── Top post (at opening near edge so it connects to upper floor railing) — placed first,
    // since flight 2's rail line ends on it ──
    const openingNearZ: number = geoMaxZ - config.openingLength;
    const topFloor: number = config.floorHeight + config.floorThickness;
    // On the slab past the opening edge, as on a straight stair: the baluster row ends at its face.
    const topPost: IBalusterGeometry = {
        position: { x: f2X, y: topFloor, z: openingNearZ - rc.postWidth / 2 },
        height: rc.height,
        rotation: 0,
    };

    const ctx: IUShapeRailingContext = {
        ...base,
        side,
        isOuter,
        halfPost,
        f1X,
        f2X,
        f1hAz: entryZ,
        f1hAy: bottomPostTopY,
        f1hBz: turnStartZ,
        f1hBy: turnPostY + rc.height,
        f2Rail: uShapeFlight2Rail(base, topPost),
        balusters: [],
        posts: [],
        handrailPoints: [],
    };

    // ── Bottom post ──
    ctx.posts.push({ position: { x: f1X, y: 0, z: entryZ }, height: bottomPostTopY, rotation: 0 });

    // ── Flight 1: handrail from bottom post top to turn entry ──
    ctx.handrailPoints.push({ x: f1X, y: ctx.f1hAy, z: ctx.f1hAz });
    pushFlight1Balusters(ctx);

    // ── Turn transition ──
    pushTurnTransition(ctx);

    ctx.posts.push(topPost);
    const topPostTopY: number = topPost.position.y + topPost.height;

    // ── Flight 2: handrail from turn exit to top post, balusters interpolated ──
    const turnExitPt: IVec3 = ctx.handrailPoints[ctx.handrailPoints.length - 1];
    ctx.handrailPoints.push({ x: topPost.position.x, y: topPostTopY, z: topPost.position.z });
    pushFlight2Balusters(ctx, turnExitPt, topPost, topPostTopY);

    return {
        side: ctx.side,
        balusters: ctx.balusters,
        posts: ctx.posts,
        handrail: { points: ctx.handrailPoints, width: rc.handrailWidth, height: rc.handrailHeight },
        balusterWidth: rc.balusterWidth,
        postWidth: rc.postWidth,
    };
}

export function generateUShapeRailings(
    flight1Steps: IStepGeometry[],
    turnSteps: IStepGeometry[],
    flight2Steps: IStepGeometry[],
    config: IStairConfig,
    turnStartZ: number,
    f2StartZ: number,
    geoMaxZ: number
): IRailingGeometry[] {
    const railings: IRailingGeometry[] = [];
    const rc: IRailingConfig = config.railing;
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';
    if (flight2Steps.length === 0) {
        return railings;
    }

    // With turnAtStep === 0 there is no flight 1 — the user steps onto the
    // winders/landing right at the entry. Anchor the railing to the first
    // available step so the bottom post and the first handrail point sit at
    // the same height (otherwise the handrail drops by one riser between the
    // post top and the turn entry).
    const sides: ('left' | 'right')[] = rc.sides === 'both' ? ['left', 'right'] : [rc.sides];
    const stepAnchors: IUShapeStepAnchors = resolveStepAnchors(flight1Steps, turnSteps, flight2Steps);
    const turnLevels: IUShapeTurnLevels = resolveTurnLevels(flight1Steps, turnSteps, stepAnchors.firstStepY, config);

    const base: IUShapeRailingBase = {
        rc,
        config,
        sw,
        isRight,
        turnStartZ,
        f2StartZ,
        geoMaxZ,
        flight1Steps,
        turnSteps,
        flight2Steps,
        ...stepAnchors,
        ...turnLevels,
    };

    for (const side of sides) {
        railings.push(buildSideRailing(base, side));
    }

    return railings;
}
